import { Buffer } from "buffer";

import * as Updates from "expo-updates";
import React, { useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react-native";
import FontAwesome from "@expo/vector-icons/FontAwesome";
import { useFonts } from "expo-font";
import { Slot, SplashScreen } from "expo-router";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { StatusBar } from "expo-status-bar";
import { Linking } from "react-native";
import Constants from "expo-constants";

import { HeaderContextProvider } from "@/context/header/HeaderContext";
import UpdateProvider from "@/context/update/UpdateProvider";
import { ExploreScreenState } from "@/schemas/screen/configs";
import { ExploreTabs, Screen } from "@/schemas/screen/tabs";
import { initializeSentry } from "@/utils/sentry";
import { SessionProvider } from "@/context/auth/AuthContext";
import { AppProvider } from "@/context/messaging/StreamContext";
import {
  initializeFirebase,
  logFirebaseInitialization,
} from "@/utils/analytics";
import { setupNotificationBeforeSigningIn } from "@/hooks/notifications";
import {
  getDeviceIdentifier,
  isNewAppInstall,
  logAppInstall,
  logDeviceData,
} from "@/hooks/device";
import LoadingScreen from "@/screens/Loading/LoadingScreen";
import { useLocationPermission } from "@/hooks/location";
import { processCampaignAttribution } from "@/hooks/campaign";
import { setupRevenueCat } from "@/hooks/revenueCat";
import { useUpdateInfo } from "@/screens/Settings/Troubleshooting/Services/utils/fetchUpdateInfo";
import { getPlatformTypeFromExpo } from "@/utils/platform";

//LogBox.ignoreAllLogs(); // Use this to ignore errors on dev on the simulator for maestro dev and testing.

const initialScreen: ExploreScreenState = {
  screenId: Screen.EXPLORE, // @deprecated
  activeTabId: ExploreTabs.NEARBY, // @deprecated
  filters: null,
};

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();
global.Buffer = Buffer;

function MobileLayout(): React.JSX.Element {
  // Add this useEffect to run the setup after initial render
  const { currentlyRunning } = Updates.useUpdates();
  const { location } = useLocationPermission();
  const updateInfo = useUpdateInfo();

  useEffect(() => {
    initializeSentry(currentlyRunning.updateId || "Unknown");
    setupNotificationBeforeSigningIn();
    setupRevenueCat(null);

    (async (): Promise<void> => {
      try {
        await processCampaignAttribution();
        await logDeviceData({
          location,
          updates: updateInfo,
          forceLogging: true,
        });
        const newInstall = await isNewAppInstall();
        if (newInstall) {
          const { deviceId } = await getDeviceIdentifier();
          await logAppInstall(deviceId);
        }
      } catch (error: unknown) {
        Sentry.captureException(error, {
          tags: { type: "campaign_attribution" },
        });
      }
    })();

    // Handle deep links while app is running
    const subscription = Linking.addEventListener("url", ({ url }) => {
      processCampaignAttribution(url);
    });

    return () => subscription?.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UpdateProvider>
      <SafeAreaProvider>
        <StatusBar style="auto" />
        <GestureHandlerRootView style={{ flex: 1 }}>
          <SessionProvider>
            <AppProvider>
              <HeaderContextProvider initialScreen={initialScreen}>
                <Slot />
              </HeaderContextProvider>
            </AppProvider>
          </SessionProvider>
        </GestureHandlerRootView>
      </SafeAreaProvider>
    </UpdateProvider>
  );
}

function RootLayout(): React.JSX.Element {
  const [loaded, error] = useFonts({
    SpaceMono: require("../assets/fonts/SpaceMono-Regular.ttf"),
    ...FontAwesome.font,
  });

  useEffect(() => {
    if (error) throw error;
  }, [error]);

  const platformType = useMemo(
    () => getPlatformTypeFromExpo(Constants.platform),
    [],
  );

  useEffect(() => {
    async function initializeApp(): Promise<void> {
      await SplashScreen.hideAsync();
      initializeFirebase(platformType);
      await logFirebaseInitialization();
    }
    void initializeApp();
  }, [loaded, platformType]);

  if (!loaded) {
    return <LoadingScreen />;
  } else {
    return <MobileLayout />;
  }
}

export default Sentry.wrap(RootLayout);
